import { action, computed, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { CloseButton } from "../Buttons/CloseButton";
import { SearchButton } from "../Buttons/SearchButton";
import { AppStore } from "../Stores/AppStore";
import { DocumentSearchStore } from "../Stores/DocumentSearchStore";
import { Strings } from "../Stores/StringsStore";
import { TypeAheadStore } from "../Stores/TypeAheadStore";
import { Urls } from "../Urls";
import { TypeAhead } from "./TypeAhead";


interface ITypeAheadSearchProps {
  searchStore?: DocumentSearchStore;
  showSuggestionList?: boolean;
  searchTermChangeCallback?: (searchTerm: string) => void;
  buttonAlignment?: number;
  placeholder?: string;
  preventSearchOnClear?: boolean;
  isDirectSearch?: boolean;
}

@observer
export class TypeAheadSearchInput extends React.Component<ITypeAheadSearchProps, {}> {

  typeAheadStore: TypeAheadStore;
  searchInput: HTMLInputElement;
  documentSearchStore: DocumentSearchStore;
  @observable showMagnifier: boolean = true;
  @observable hasChanged: boolean = false;

  /*--------------component methods------------*/
  componentWillMount() {
    if (this.props.searchStore) {
      this.documentSearchStore = this.props.searchStore;
      this.typeAheadStore = this.props.searchStore.typeAheadStore;
    }
    else {
      this.typeAheadStore = new TypeAheadStore();
    }
  }

  componentDidMount() {
    if (typeof this.props.showSuggestionList !== 'undefined') {
      this.typeAheadStore.showSuggestions = this.props.showSuggestionList;
    }
    if (this.props.searchTermChangeCallback) {
      this.typeAheadStore.searchTermChangeCallback = this.props.searchTermChangeCallback;
    }
  }

  componentDidUpdate(prevProps: ITypeAheadSearchProps) {
    if (this.props != prevProps) {
      if (this.typeAheadStore.searchTerm && (this.props.searchStore.typeAheadStore.searchTerm !== prevProps.searchStore.typeAheadStore.searchTerm)) {
        this.typeAheadStore.copyToUndoBuffer();
      }
    }
  }

  private redirectToSearch() {
    if (this.props.isDirectSearch) {
      this.typeAheadStore.searchTerm = this.getWildCardSearchString(this.typeAheadStore.searchTerm);
    }
    if (this.typeAheadStore.showSuggestions) {
      AppStore.keepOnPage(Urls.search + "?searchTerm=" + this.typeAheadStore.searchTerm, true);
    }
    else if (this.props.searchTermChangeCallback) {
      this.props.searchTermChangeCallback(this.typeAheadStore.searchTerm);
    }
  }

  private getWildCardSearchString(searchTerm: string) {
    if (AppStore.context.documentWildCardSearch) {
      if ((searchTerm.startsWith('"') && searchTerm.endsWith('"')) || searchTerm.endsWith('*')) {
        return searchTerm;
      }
      else {
        if (!searchTerm.endsWith('*') && searchTerm.length > 0) {
          return searchTerm + '*';
        }
        return searchTerm;
      }
    }
    else {
      return searchTerm;
    }
  }
  /**
  * when a type ahead list item clicks
  */
  @action handleSelectValue = (value: string) => {
    this.typeAheadStore.searchTerm = value;
    this.searchInput.focus();
    if (this.documentSearchStore) {
      this.documentSearchStore.searchTerm = this.typeAheadStore.searchTerm;
      this.documentSearchStore.triggerSearch(true);
    }
    else {
      this.redirectToSearch();
    }
    this.hasChanged = false;
  }

  //for getting arrow up/down we need onKeyDown
  @action handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // CTRL+Z: undo
    if (event.charCode === 90 && event.ctrlKey) {
      event.preventDefault();
      event.stopPropagation();
      this.typeAheadStore.undoRedoHandler(1);
      return;
    }
    // CTRL+Y: redo
    if (event.charCode === 89 && event.ctrlKey) {
      event.preventDefault();
      event.stopPropagation();
      this.typeAheadStore.undoRedoHandler(-1);
      return;
    }
    // ESC: reset and hide type ahead suggestion list
    if (event.keyCode === 27) {
      event.preventDefault();
      event.stopPropagation();
      this.typeAheadStore.resetSuggestions();
      return;
    }
    // UP/DOWN: fech and change selection
    if (event.keyCode === 38 || event.keyCode === 40) {
      event.preventDefault();
      event.stopPropagation();
      let length = this.typeAheadStore.suggestions.length;
      if (length > 0) {
        this.typeAheadStore.suggestionIndex = (this.typeAheadStore.suggestionIndex + length + (event.keyCode - 39)) % length;
        this.typeAheadStore.searchTerm = this.typeAheadStore.suggestions[this.typeAheadStore.suggestionIndex].value;
        this.typeAheadStore.copyToUndoBuffer();
      }
      return;
    }
    // ENTER: trigger search
    if (event.keyCode === 13 ) {
      event.preventDefault();
      event.stopPropagation();

      if (this.documentSearchStore) {
        let serachTerm: string = this.getWildCardSearchString(event.currentTarget.value);
        this.typeAheadStore.searchTerm = serachTerm;
        this.documentSearchStore.searchTerm = serachTerm;
      

      if (this.typeAheadStore.suggestionIndex == -1 && this.typeAheadStore.suggestions.length >= 0) {
        this.documentSearchStore.triggerSearch(true);
      }
      else if (this.typeAheadStore.suggestions.length > 0 && this.typeAheadStore.suggestionIndex >= 0) {
        this.typeAheadStore.searchTerm = this.typeAheadStore.suggestions[this.typeAheadStore.suggestionIndex].value;
        this.documentSearchStore.searchTerm = this.typeAheadStore.searchTerm;
        this.documentSearchStore.triggerSearch(true);
      }
    }
    else {
      this.redirectToSearch();
    }
    this.hasChanged = false;
  }
}

@action handleMouseDown = (event: React.MouseEvent<HTMLLIElement>) => {
  if (!event.ctrlKey && !event.altKey && !event.metaKey && !event.shiftKey && event.button === 0) {
    event.preventDefault();
    event.stopPropagation();
    const value = event.currentTarget.getAttribute("data-value");
    this.handleSelectValue(value);
  }
}

/**
 * when user insert characters in search field
 */
@action handleSearchTermChange = (event: React.FormEvent<HTMLInputElement>) => {
  this.typeAheadStore.isLoading = true;
  this.typeAheadStore.searchTerm = event.currentTarget.value;
  this.typeAheadStore.copyToUndoBuffer();
  if (this.documentSearchStore) {
    this.documentSearchStore.searchTerm = this.typeAheadStore.searchTerm;
  }
  clearTimeout(this.typeAheadStore.searchTimeoutHandle);
  this.typeAheadStore.searchTimeoutHandle = setTimeout(() => this.typeAheadStore.loadSuggestions(), 250);
  this.hasChanged = true;
}

@action handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
  this.typeAheadStore.resetSuggestions();
  this.showMagnifier = true;
}

@action handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
  this.showMagnifier = false;
}

@computed get buttonAlignmentClass(): string {
  switch (this.props.buttonAlignment) {
    case 1:
      return "custom-typeahead is--one";
    case 2:
      return "custom-typeahead is--two";
    case 3:
      return "custom-typeahead is--three";
    default:
      return "custom-typeahead is--full";
  }
}

handleClear = () => {
  this.typeAheadStore.searchTerm = "";
  if (this.documentSearchStore) {
    this.documentSearchStore.searchTerm = this.typeAheadStore.searchTerm;
  }
  if (!this.props.preventSearchOnClear) {
    this.search();
  }
}

/**
* search button click handler
*/
search = () => {
  if (this.documentSearchStore) {
    this.documentSearchStore.triggerSearch(true);
  }
  else {
    this.redirectToSearch();
  }
  this.hasChanged = false;
}

render() {
  return <div className={this.buttonAlignmentClass}>
    <div className="typeahead-wrapper">
      {this.showMagnifier ? <SearchButton testId="searchButton" /> : null}
      <input spellCheck={false} ref={elem => this.searchInput = elem} type="text" data-test-id="searchInput"
        placeholder={this.props.placeholder || Strings.general.searchPlaceholder} value={this.typeAheadStore.searchTerm}
        onInput={this.handleSearchTermChange} onKeyDown={this.handleKeyDown} onBlur={this.handleBlur} onFocus={this.handleFocus} onChange={() => { }} />
      {this.typeAheadStore.searchTerm ? <CloseButton onClick={this.handleClear} /> : null}
      <TypeAhead selectedIndex={this.typeAheadStore.suggestionIndex} searchResult={this.typeAheadStore.suggestions}
        handleMouseDown={this.handleMouseDown} showNoResultsMessage={this.props.searchStore && !this.showMagnifier && this.typeAheadStore.searchTerm && !this.typeAheadStore.isLoading && this.hasChanged} />
    </div>
  </div>;
}
}
